import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Tag from '@amzn/meridian/tag';
import Text from '@amzn/meridian/text';
import LoadingSpinner from 'src/components/shared/loadingSpinner/loadingSpinner';
import {
  generateJobMatches,
  setIsCancelModalOpen,
  setIsUsingNewExperience,
  setPageProgress,
} from 'src/store/newJmrPageSlice/newJmrPageSlice';
import { useAppDispatch, useAppSelector } from 'src/store/store';
import { JmrFlowTypes, JmrPageProgress } from 'src/utils/jmrUtils';
import {
  areAllUsedHoursMoreThanZero,
  calculateUnusedTotalHours,
  getRequirementSumsAndJobs,
  isTotalUnusedHoursValid,
  toggleJob,
} from 'src/utils/placementSelectionUtils';

import { NewJMRPageFooter, NewJMRPageMainBody, NewJMRPageWrapper } from '../pageLayout/pageLayout';
import ResultsPageLegacy from '../resultsPage/resultsPageLegacy/resultsPageLegacy';
import '../resultsPage/resultsPageLegacy/resultsPageLegacy.scss';

import ConflictErrorBanner from './conflictErrorBanner/conflictErrorBanner';
import JobMatchesTable from './jobMatches/jobMatchesTable';
import TopOptions from './topOptions/topOptions';

const ResultsPage = () => {
  /** For scrolling selected job row into view via clicking footer Tag */
  const selectedJobRefs = useRef(new Map<string, HTMLElement>());
  const scrollJobRowIntoView = (id: string) => {
    const inputRef = selectedJobRefs.current.get(id);
    if (inputRef) {
      // TODO if sticky content is not larger than half the view height, use block: 'center'
      inputRef.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };

  const dispatch = useAppDispatch();
  const { featureFlags } = useAppSelector((state) => state.user);
  const { allowedSites } = useAppSelector((state) => state.sites);
  const { backgroundFormData, isLoadingGetPlacementDetails, jmrResults, isUsingNewExperience, jmrFlowType } =
    useAppSelector((state) => state.newJmrPage);
  const [isLoading, setIsLoading] = useState(true);
  const [actualShiftLength, setActualShiftLength] = useState(0);
  const [searchField, setSearchField] = useState('');

  // Check criteria to determine whether to use the new experience
  const determineUsingNewExperience = () =>
    featureFlags.JOB_MODIFICATIONS &&
    allowedSites.some((site) => site.site === backgroundFormData.siteLocation && site.jobModificationsEnabled);

  const loadResults = async () => {
    try {
      const isNewResultsPgExperienceEnabled = determineUsingNewExperience();
      await dispatch(generateJobMatches(isNewResultsPgExperienceEnabled ? 'v2' : 'v1'));
      dispatch(setIsUsingNewExperience(isNewResultsPgExperienceEnabled));
      setActualShiftLength(
        backgroundFormData.cannotWorkNormalShiftHrs
          ? +backgroundFormData.limitedHoursPerDay!
          : backgroundFormData.shiftLength,
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Call generate job matches API matches
  useEffect(() => {
    // If coming to this page from update placments flow, ensure the existing placement's background information
    // and restrictions have been loaded before fetching job matches
    if (backgroundFormData.alias && !isLoadingGetPlacementDetails && allowedSites.length) {
      loadResults();
    }
    return () => {
      setIsLoading(true);
    };
  }, [backgroundFormData, isLoadingGetPlacementDetails, allowedSites]);

  const {
    selectedJobs,
    numSelected,
    totalSelectedHours,
    exceededJobIds,
    exceededRestrictionIds,
    exceededRestrictionNames,
  } = useMemo(() => {
    if (!jmrResults.length) {
      return {
        selectedJobs: [],
        numRows: 0,
        totalSelectedHours: 0,
        exceededRequirementDetails: [],
        exceededJobIds: new Set<string>(),
        exceededRestrictionIds: new Set<number>(),
        exceededRestrictionNames: [],
      };
    }
    const selectedJobs = jmrResults.filter((row) => row.isChecked);
    const requirementDetails = getRequirementSumsAndJobs(selectedJobs); // excludes modified requirements
    const exceededRequirementDetails = requirementDetails.filter((rd) => rd.restrictedHours < rd.totalHours);
    return {
      selectedJobs,
      numSelected: selectedJobs.length,
      totalSelectedHours: selectedJobs.reduce((acc, row) => acc + (+row?.totalHours || 0), 0),
      // for displaying in error banner
      exceededRestrictionNames: exceededRequirementDetails.map((erd) => erd.taskName),
      // for highlighting job's requirements in popover and modifications modal
      exceededRestrictionIds: new Set(exceededRequirementDetails.map((erd) => erd.taskId)),
      // for highlighting table rows and filtering selected jobs in error banner
      exceededJobIds: new Set(exceededRequirementDetails.flatMap((erd) => erd.contributingJobs).map((j) => j.id)),
    };
  }, [jmrResults]);

  // query the size of the sticky content. Will change if a ConflictErrorBanner is displayed. Is used for dynamically
  // setting meridian Table sticky header offset
  const stickyContentRef = useRef<HTMLDivElement>(null);
  const [stickyHeight, setStickyHeight] = useState(0);
  useLayoutEffect(() => {
    if (stickyContentRef.current) {
      setStickyHeight(stickyContentRef.current.offsetHeight);
    }
  }, [isLoading, exceededRestrictionNames.length, exceededJobIds.size]);

  const validateSelectedJobs = () => {
    const selectedJobs = jmrResults.filter((jmrResult) => jmrResult.isChecked === true);
    const { shiftLength, limitedHoursPerDay } = backgroundFormData;
    if (!actualShiftLength || !selectedJobs.length) {
      return false;
    }
    const newTotalUnusedHours = calculateUnusedTotalHours(selectedJobs, shiftLength, +limitedHoursPerDay!);
    const isEveryJobWithinRestrictions = selectedJobs
      // Filter for unmodified jobs
      .filter((s) => !s.conflicts || s.conflicts.every((c) => !c.appliedModifications?.length))
      // The alloted hours for any such job must be within the maximum hours allowed
      .every((s) => s.maximumHoursAssociateCanDo === undefined || +s.totalHours <= s.maximumHoursAssociateCanDo);

    return (
      isTotalUnusedHoursValid(newTotalUnusedHours, shiftLength, +limitedHoursPerDay!) &&
      areAllUsedHoursMoreThanZero(selectedJobs) &&
      isEveryJobWithinRestrictions &&
      !exceededRestrictionIds.size
    );
  };

  return !isLoading ? (
    <>
      {isUsingNewExperience ? (
        <NewJMRPageWrapper>
          {/* Main Content */}
          <NewJMRPageMainBody
            xs="85%"
            md="75%"
            lg="70%"
            title="Select Job Matches"
            subtitle="Jobs are ranked according to the associates restrictions and ability to perform the job."
          >
            <div ref={stickyContentRef} className="new-jmr__results__sticky-content">
              <ConflictErrorBanner
                onClickJob={scrollJobRowIntoView}
                {...{ selectedJobs, exceededJobIds, exceededRestrictionNames, exceededRestrictionIds }}
              />
              <TopOptions searchField={searchField} setSearchField={setSearchField} />
            </div>
            <JobMatchesTable
              {...{
                stickyHeight,
                searchField,
                jobsExceedingRestrictionsInAggregate: exceededJobIds,
                exceededRestrictionIds,
                selectedJobRefs,
              }}
            />
          </NewJMRPageMainBody>

          {/* Footer */}
          <NewJMRPageFooter
            leftSideContent={
              <Column spacing={'100'}>
                <Text>{`${numSelected} selected ∙ ${totalSelectedHours} of ${actualShiftLength} hrs${numSelected ? ':' : ''}`}</Text>
                <Row spacing={'200'} wrap="down" maxWidth={600}>
                  {jmrResults
                    .filter((r) => r.isChecked)
                    .map((r) => (
                      <Tag
                        key={r.id}
                        data-testid="newJmr__results__tag"
                        type="neutral"
                        onClose={() => toggleJob(r.id, jmrResults, dispatch)}
                      >
                        {r.name}
                      </Tag>
                    ))}
                </Row>
              </Column>
            }
          >
            <Button type="link" onClick={() => dispatch(setIsCancelModalOpen(true))}>
              Cancel
            </Button>
            <Button
              type="tertiary"
              disabled={jmrFlowType === JmrFlowTypes.AlternativePlacementRecommended}
              onClick={() => dispatch(setPageProgress(JmrPageProgress.Restrictions))}
            >
              Previous
            </Button>
            <Button
              data-cy="new-jmr__results__next"
              disabled={!validateSelectedJobs()}
              onClick={() => dispatch(setPageProgress(JmrPageProgress.Review))}
            >
              Next
            </Button>
          </NewJMRPageFooter>
        </NewJMRPageWrapper>
      ) : (
        <Column className="new-jmr-v1" alignmentHorizontal="center">
          <ResultsPageLegacy />
        </Column>
      )}
    </>
  ) : (
    <LoadingSpinner />
  );
};

export default ResultsPage;
